import {
  Box,
  Typography,
  MenuItem,
  Button,
  InputAdornment,
  TextFieldProps,
  styled,
} from "@mui/material";
import { ReactElement } from "react";

import { FlexBox } from "~frontend/components/FlexBox";
import { FormikSelect } from "~frontend/components/forms/FormSelect";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import theme, { palette } from "~frontend/theme";

import ContactImportFormSubmitButtons from "../ContactImportFormSubmitButtons";

const AddATargetButton = styled(Button)({
  backgroundColor: palette.midGray,
  color: palette.navy,
  letterSpacing: "0.4px",
  fontSize: "10px",
  padding: "6px 8px",
  lineHeight: 1,
  height: "auto",
});

const DeleteTargetButton = styled(Button)({
  color: palette.navy,
  border: `1px solid ${palette.midGray}`,
  letterSpacing: "0.4px",
  fontSize: "10px",
  padding: "6px 8px",
  lineHeight: 1,
  height: "auto",
});

const dollarAdornment: TextFieldProps["InputProps"] = {
  startAdornment: (
    <InputAdornment
      position="start"
      sx={{ marginLeft: 1, marginRight: -1, marginTop: 0 }}
    >
      <Typography display="inline" sx={{ fontSize: "1.0em" }}>
        $
      </Typography>
    </InputAdornment>
  ),
};

function OrBar(): ReactElement {
  return (
    <Box sx={{ display: "block", padding: `${theme.spacing(5)} 0` }}>
      <Box sx={{ borderBottom: `2px solid ${palette.midGray}`, lineHeight: 0 }}>
        &nbsp;
      </Box>{" "}
      <Typography
        variant="h3Caps"
        sx={{
          color: palette.darkGray,
          marginTop: "-10px",
          position: "absolute",
          backgroundColor: palette.lightestGray,
          paddingRight: theme.spacing(1.5),
        }}
        component="span"
      >
        OR
      </Typography>
    </Box>
  );
}

export function FilterForm({
  index,
  onDelete,
  onAdd,
  readOnly,
  resetContactSourceSettings,
}: {
  index: number;
  onDelete: ((index: number) => undefined) | null;
  onAdd: (() => void) | null;
  readOnly?: boolean;
  resetContactSourceSettings: (() => void) | null;
}): ReactElement {
  return (
    <>
      {index > 0 ? <OrBar /> : null}
      <Box key={`act-blue-filter-${index}`}>
        <Typography variant="body1Bold">Filter by donation amount:</Typography>
        <FlexBox
          gap={theme.spacing(3)}
          sx={{
            marginBottom: theme.spacing(2),
          }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <FormikTextField
              name={`filters[${index}].donationMinimum`}
              label="Donation minimum"
              InputProps={dollarAdornment}
              fullWidth={true}
              disabled={readOnly}
            />
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <FormikTextField
              name={`filters[${index}].donationMaximum`}
              label="Donation maximum"
              InputProps={dollarAdornment}
              fullWidth={true}
              disabled={readOnly}
            />
          </Box>
        </FlexBox>
        <Typography variant="body1Bold">Filter by donation type:</Typography>
        <FlexBox
          gap={theme.spacing(3)}
          sx={{
            marginBottom: theme.spacing(2),
          }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <FormikSelect
              name={`filters[${index}].deviceType`}
              label="Donation Device"
              fullWidth={true}
              disabled={readOnly}
            >
              <MenuItem key={`anyDevice${index}`} value="">
                Any
              </MenuItem>
              <MenuItem key={`abFilterMobile${index}`} value="MOBILE">
                Mobile
              </MenuItem>
              <MenuItem key={`abFilterDesktop${index}`} value="DESKTOP">
                Desktop
              </MenuItem>
            </FormikSelect>
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <FormikSelect
              name={`filters[${index}].donationType`}
              label="Donation Type"
              fullWidth={true}
              disabled={readOnly}
            >
              <MenuItem key={`anyDonation${index}`} value="">
                Any
              </MenuItem>
              <MenuItem key={`abFilterOneTime${index}`} value="ONE_TIME">
                Non-recurring donation
              </MenuItem>
              <MenuItem key={`abFilterRecurring${index}`} value="RECURRING">
                Recurring donation
              </MenuItem>
            </FormikSelect>
          </Box>
        </FlexBox>
        <Typography variant="body1Bold">Filter by refcode:</Typography>
        <Box>
          <FormikTextField
            name={`filters[${index}].refcodes`}
            label="Refcodes"
            bottomLabel="Enter a comma separated list of refcode values to target"
            fullWidth={true}
            disabled={readOnly}
          />
        </Box>
        {onAdd && (
          <Typography variant="body1" sx={{ marginBottom: theme.spacing(3) }}>
            <strong>Note:</strong> ActBlue donors that have explicitly opted out
            of text messages will be filtered out by the import process.
          </Typography>
        )}
        {onDelete || onAdd ? (
          <FlexBox
            sx={{
              marginTop: theme.spacing(2),
              justifyContent: "space-between",
            }}
          >
            <FlexBox
              sx={{
                justifyContent: "flex-start",
                gap: theme.spacing(1),
              }}
            >
              {onDelete ? (
                <DeleteTargetButton
                  aria-label="remove target filters"
                  onClick={() => onDelete(index)}
                >
                  Delete target
                </DeleteTargetButton>
              ) : null}
              {onAdd ? (
                <>
                  <AddATargetButton onClick={() => onAdd()}>
                    + Add another target
                  </AddATargetButton>
                </>
              ) : (
                <>&nbsp;</>
              )}
            </FlexBox>
            {!readOnly && !!onAdd && (
              <ContactImportFormSubmitButtons
                resetContactSourceSettings={resetContactSourceSettings}
              />
            )}
          </FlexBox>
        ) : null}
      </Box>
    </>
  );
}
